// Not a record (SPEC F6, v1.1): the reasons, and the tuning export for Dig › Channels. The export is for tuning the
// SESSION_TITLE and NOT_MUSIC_TITLE word lists by hand; it carries titles (API data, 30-day rule), so it is a local file,
// never committed.

import { crateExpired, type CrateShard } from './dig';

export type NotRecordReason = 'talk' | 'new-session' | 'cover' | 'other';

export const NOT_RECORD_REASONS: { value: NotRecordReason; label: string }[] = [
  { value: 'talk', label: 'Talk' },
  { value: 'new-session', label: 'New session' },
  { value: 'cover', label: 'Cover' },
  { value: 'other', label: 'Other' },
];

export interface NotRecordLine {
  channel: string;
  reason: NotRecordReason;
  /** Undefined once the record has left the crate or its shard is past 30 days */
  title?: string;
}

/** Joins marked records with the crate shards that are still allowed (under 30 days old) for channel and title. */
export function notRecordLines(marked: readonly { videoId: string; reason: NotRecordReason }[], shards: readonly CrateShard[], now: number): NotRecordLine[] {
  const found = new Map<string, { channel: string; title: string }>();
  for (const shard of shards) {
    if (crateExpired(shard, now)) continue;
    for (const r of shard.records) {
      const ref = shard.channels[r[2]];
      if (ref && !found.has(r[0])) found.set(r[0], { channel: ref[1], title: r[1] });
    }
  }
  return marked
    .map((m) => ({ channel: found.get(m.videoId)?.channel ?? 'Not in the current crate', reason: m.reason, title: found.get(m.videoId)?.title }))
    .sort((a, b) => a.channel.localeCompare(b.channel) || a.reason.localeCompare(b.reason) || (a.title ?? '').localeCompare(b.title ?? ''));
}

/** Plain text: counts per channel and reason first, then every title. */
export function notRecordExport(lines: readonly NotRecordLine[]): string {
  const counts = new Map<string, number>();
  for (const l of lines) counts.set(`${l.channel}\t${l.reason}`, (counts.get(`${l.channel}\t${l.reason}`) ?? 0) + 1);
  const out = ['Not a record: counts per channel and reason', ''];
  for (const [k, n] of [...counts].sort(([a], [b]) => a.localeCompare(b))) out.push(`${k.replace('\t', ' | ')} | ${n}`);
  out.push('', 'Titles', '');
  for (const l of lines) out.push(`${l.channel} | ${l.reason} | ${l.title ?? '(title not available)'}`);
  return `${out.join('\n')}\n`;
}
